import { ArrowsDownUp, Eye, DownloadSimple, Info } from '@phosphor-icons/react';
import { AccentButtonOutline } from '../../../../../../shared/Buttons';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { InternalBankAccount } from '../../../../../../types/dbTypes';
import { useState, useRef } from 'react';
import { ClipLoader } from 'react-spinners';
import { DemoDataWrapper } from '../../home/components/DemoDataBadge';
import toast from 'react-hot-toast';
import useApi from '../../../../../../utils/AxiosWrapper';
import { Tooltip } from 'antd';
import { ACH_WIRE_TOOLTIP } from '../../../../../../constants/bankingCopy';

type AccountMode = 'standard' | 'wire';

const VOIDED_CHECK_WIRE_DISABLED_TOOLTIP =
  'Voided check only has ACH information. Do not use for wire transfers.';

function AccountWidget({
  onTransfer,
  accountInfo,
  loadingAccountInfo,
  isPreActive = false,
}: {
  onTransfer: () => void;
  accountInfo: InternalBankAccount;
  loadingAccountInfo: boolean;
  isPreActive?: boolean;
}) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [downloadingVoidedCheck, setDownloadingVoidedCheck] = useState(false);
  const [accountMode, setAccountMode] = useState<AccountMode>('standard');
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(
    null
  );
  const detailsRef = useRef<HTMLDivElement>(null);

  const handleModeChange = (mode: AccountMode) => {
    if (mode === accountMode) return;
    setSlideDirection(mode === 'wire' ? 'left' : 'right');
    setAccountMode(mode);
    // Remove animation class after it completes so re-renders don't retrigger it
    setTimeout(() => setSlideDirection(null), 250);
  };

  const maskAccountNumber = (accountNumber: string) => {
    if (!accountNumber) return '';
    return (
      '*'.repeat(Math.max(0, accountNumber.length - 4)) +
      accountNumber.slice(-4)
    );
  };

  // Resolve which account/routing to display based on toggle
  const displayAccountNumber =
    accountMode === 'wire'
      ? accountInfo?.wire_account_number
      : accountInfo?.account_number;

  const displayRoutingNumber =
    accountMode === 'wire'
      ? accountInfo?.wire_routing_number
      : accountInfo?.routing_number;

  const fallbackRouting = '053101561';

  const downloadVoidedCheck = async () => {
    if (isPreActive) {
      toast.error('Complete onboarding to download your voided check.');
      return;
    }

    if (!accountInfo?.account_id) {
      toast.error(
        'Unable to download voided check. Account information is missing.'
      );
      return;
    }

    setDownloadingVoidedCheck(true);
    try {
      const response = await callProtectedApi(
        `/documents/display/voidedCheck/${accountInfo.account_id}`,
        'post',
        { document_count: 1 },
        'Error occurred while downloading your voided check.'
      );

      const presignedUrl = response?.data?.data?.document_urls?.[0];
      if (!presignedUrl) {
        throw new Error('Voided check not available.');
      }
      // Fetch the file and trigger download with custom filename
      const fileResponse = await fetch(presignedUrl);
      if (!fileResponse.ok) {
        throw new Error('Failed to download file');
      }

      // Get the content type from the response to determine file extension
      const contentType =
        fileResponse.headers.get('content-type') || 'application/pdf';
      const extension = contentType.includes('pdf')
        ? 'pdf'
        : contentType.split('/')[1] || 'pdf';

      // Convert response to blob and trigger download
      const blob = await fileResponse.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `TACH Account Voided Check.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Voided check downloaded successfully.', {
        duration: 5000,
      });
    } catch (error: any) {
      console.error('Error downloading voided check:', error);
      toast.error('Failed to download voided check. Please contact support.');
    } finally {
      setDownloadingVoidedCheck(false);
    }
  };

  const hasWireInfo =
    !!accountInfo?.wire_account_number && !!accountInfo?.wire_routing_number;

  const isWire = accountMode === 'wire';

  // Toggle component
  const AccountModeToggle = ({ compact = false }: { compact?: boolean }) => (
    <div className="flex items-center gap-2">
      <div className="flex rounded-full border border-bordergray bg-[#F5F5F7] p-[2px]">
        <button
          onClick={() => handleModeChange('standard')}
          className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-all ${
            accountMode === 'standard'
              ? 'bg-white text-primary shadow-sm'
              : 'text-[#858897] hover:text-primary'
          }`}
        >
          Standard (ACH)
        </button>
        <button
          onClick={() => {
            if (!hasWireInfo) {
              toast.error(
                'Wire details are not yet available for your account.'
              );
              return;
            }
            handleModeChange('wire');
          }}
          className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-all ${
            accountMode === 'wire'
              ? 'bg-white text-primary shadow-sm'
              : 'text-[#858897] hover:text-primary'
          } ${!hasWireInfo ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          Wire
        </button>
      </div>
      <Tooltip
        title={ACH_WIRE_TOOLTIP}
        placement="top"
        overlayStyle={{ maxWidth: 260, fontSize: 12 }}
      >
        <Info size={14} className="cursor-help text-[#858897]" />
      </Tooltip>
    </div>
  );

  // Account details section — shared across all screen sizes
  // Uses key={accountMode} to remount and trigger the slide animation on toggle
  const AccountDetails = ({
    layout = 'vertical',
    compact = false,
  }: {
    layout?: 'vertical' | 'horizontal';
    compact?: boolean;
  }) => (
    <div
      ref={detailsRef}
      className={`flex ${layout === 'horizontal' ? 'flex-row items-center justify-evenly' : 'flex-col space-y-4'} w-full`}
      style={{
        animation: slideDirection
          ? `accountSlide${slideDirection === 'left' ? 'Left' : 'Right'} 0.25s ease-out`
          : 'none',
      }}
    >
      <style>{`
        @keyframes accountSlideLeft {
          from { opacity: 0; transform: translateX(-12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes accountSlideRight {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div className="flex flex-col space-y-2">
        <h2
          className={`${compact ? 'text-[13px]' : 'text-[16px]'} text-[#858897]`}
        >
          {accountMode === 'wire' ? 'Wire Account Number' : 'Account Number'}
        </h2>
        <div className="flex flex-row items-center space-x-2 align-middle">
          <h2
            className={`${compact ? 'text-[13px] leading-[20px]' : 'text-[16px] leading-[24px]'} text-primary`}
          >
            {showAccountNumber
              ? (displayAccountNumber ?? '')
              : maskAccountNumber(displayAccountNumber ?? '')}
          </h2>
          <Eye
            className={`mb-[5px] cursor-pointer ${compact ? 'text-[13px]' : 'text-[16px]'} text-primary`}
            onClick={() => {
              if (isPreActive) {
                toast.error(
                  'Please complete your onboarding steps to access this.'
                );
                return;
              }
              setShowAccountNumber(!showAccountNumber);
            }}
          />
        </div>
      </div>
      <div className="flex flex-col space-y-2">
        <h2
          className={`${compact ? 'text-[13px]' : 'text-[16px]'} text-[#858897]`}
        >
          {accountMode === 'wire' ? 'Wire Routing Number' : 'Routing Number'}
        </h2>
        <div className="flex flex-row items-center space-x-2 align-middle">
          <h2
            className={`${compact ? 'text-[13px] leading-[20px]' : 'text-[16px] leading-[24px]'} text-primary`}
          >
            {!displayRoutingNumber || displayRoutingNumber.length === 0
              ? fallbackRouting
              : displayRoutingNumber}
          </h2>
        </div>
      </div>
    </div>
  );

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="flex h-[385px] max-h-[385px] w-full max-w-[375px] flex-col items-center justify-between gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Your TACH Account
              </h2>
            </div>
            {!loadingAccountInfo ? (
              <div className="flex w-full flex-col items-start justify-start space-y-4 overflow-x-auto overflow-y-hidden p-4">
                <AccountModeToggle />
                <AccountDetails layout="vertical" />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingAccountInfo}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full items-center justify-center"
                />
              </div>
            )}
            {!loadingAccountInfo && (
              <div className="flex h-[20%] w-full flex-row items-center justify-center space-x-2 border-t-[1px] border-t-bordergray p-4 align-middle">
                <AccentButtonOutline
                  onClick={onTransfer}
                  disabled={isPreActive}
                  addClass={`py-[4px] px-4 rounded-full h-[36px] flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <div className="flex flex-row items-center space-x-2 whitespace-nowrap align-middle">
                    <ArrowsDownUp size={16} />
                    <p>Transfer</p>
                  </div>
                </AccentButtonOutline>
                <Tooltip
                  title={isWire ? VOIDED_CHECK_WIRE_DISABLED_TOOLTIP : ''}
                  placement="top"
                  overlayStyle={{ maxWidth: 260, fontSize: 12 }}
                >
                  <span style={{ display: 'inline-flex' }}>
                    <AccentButtonOutline
                      onClick={downloadVoidedCheck}
                      disabled={isPreActive || downloadingVoidedCheck || isWire}
                      addClass={`py-[4px] px-4 rounded-full h-[36px] flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      <div className="flex flex-row items-center space-x-2 whitespace-nowrap align-middle">
                        {downloadingVoidedCheck ? (
                          <ClipLoader color="#1D213B" size={16} />
                        ) : (
                          <DownloadSimple size={16} />
                        )}
                        <p>ACH Voided Check</p>
                      </div>
                    </AccentButtonOutline>
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        );
      case 'tablet':
        return (
          <div className="flex h-[265px] max-h-[265px] w-[568px] max-w-[568px] flex-col items-center justify-between gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-3 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Your TACH Account
              </h2>
            </div>
            {!loadingAccountInfo ? (
              <div className="flex w-full flex-col gap-3 overflow-x-auto overflow-y-hidden p-3">
                <AccountModeToggle />
                <AccountDetails layout="horizontal" />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingAccountInfo}
                  size={40}
                />
              </div>
            )}
            <div className="flex h-[30%] w-full flex-row items-center justify-center space-x-4 border-t-[1px] border-t-bordergray p-3 align-middle">
              <AccentButtonOutline
                addClass={`py-[8px] px-6 rounded-full h-[40px] disabled:cursor-not-allowed disabled:opacity-60`}
                onClick={onTransfer}
                disabled={isPreActive}
              >
                <div className="flex flex-row items-center space-x-2 whitespace-nowrap align-middle">
                  <ArrowsDownUp size={18} />
                  <p>Transfer</p>
                </div>
              </AccentButtonOutline>
              <Tooltip
                title={isWire ? VOIDED_CHECK_WIRE_DISABLED_TOOLTIP : ''}
                placement="top"
                overlayStyle={{ maxWidth: 260, fontSize: 12 }}
              >
                <span style={{ display: 'inline-flex' }}>
                  <AccentButtonOutline
                    addClass={`py-[8px] px-6 rounded-full h-[40px] disabled:cursor-not-allowed disabled:opacity-60`}
                    onClick={downloadVoidedCheck}
                    disabled={isPreActive || downloadingVoidedCheck || isWire}
                  >
                    <div className="flex flex-row items-center space-x-2 whitespace-nowrap align-middle">
                      {downloadingVoidedCheck ? (
                        <ClipLoader color="#1D213B" size={18} />
                      ) : (
                        <DownloadSimple size={18} />
                      )}
                      <p>ACH Voided Check</p>
                    </div>
                  </AccentButtonOutline>
                </span>
              </Tooltip>
            </div>
          </div>
        );
      case 'mobile':
        return (
          <div className="flex h-[265px] max-h-[265px] w-[343px] max-w-[343px] flex-col items-center justify-between gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-3 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Your TACH Account
              </h2>
            </div>
            {!loadingAccountInfo ? (
              <div className="flex w-full flex-col gap-3 overflow-x-auto overflow-y-hidden p-3">
                <AccountModeToggle compact />
                <AccountDetails layout="horizontal" compact />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingAccountInfo}
                  size={30}
                />
              </div>
            )}
            <div className="flex h-[30%] w-full flex-row items-center justify-center space-x-2 border-t-[1px] border-t-bordergray p-3 align-middle">
              <AccentButtonOutline
                addClass={`py-[8px] px-4 rounded-full h-[36px] disabled:cursor-not-allowed disabled:opacity-60`}
                onClick={onTransfer}
                disabled={isPreActive}
              >
                <div className="flex flex-row items-center space-x-1 whitespace-nowrap align-middle">
                  <ArrowsDownUp size={16} />
                  <p className="text-[13px]">Transfer</p>
                </div>
              </AccentButtonOutline>
              <Tooltip
                title={isWire ? VOIDED_CHECK_WIRE_DISABLED_TOOLTIP : ''}
                placement="top"
                overlayStyle={{ maxWidth: 260, fontSize: 12 }}
              >
                <span style={{ display: 'inline-flex' }}>
                  <AccentButtonOutline
                    addClass={`py-[8px] px-3 rounded-full h-[36px] disabled:cursor-not-allowed disabled:opacity-60`}
                    onClick={downloadVoidedCheck}
                    disabled={isPreActive || downloadingVoidedCheck || isWire}
                  >
                    <div className="flex flex-row items-center space-x-1 whitespace-nowrap align-middle">
                      {downloadingVoidedCheck ? (
                        <ClipLoader color="#1D213B" size={16} />
                      ) : (
                        <DownloadSimple size={16} />
                      )}
                      <p className="text-[13px]">ACH Voided Check</p>
                    </div>
                  </AccentButtonOutline>
                </span>
              </Tooltip>
            </div>
          </div>
        );
    }
  };

  return (
    <DemoDataWrapper showBadge={isPreActive}>
      {renderScreenView(screenView)}
    </DemoDataWrapper>
  );
}

export default AccountWidget;
